import moment from 'moment';
import { startCase, toLower } from 'lodash';
import { IUser } from 'stubs/graphql/user';
import { ILOV } from 'stubs/graphql/lov';
import { IItem } from 'stubs/graphql/swap';

export const itemImageBaseURL = `${process.env.REACT_APP_ASSETS_URL}/img/item/`;

export enum TXN_TYPE {
  in = 'in',
  out = 'out',
  seed = 'seed',
  adjustment = 'adj',
}

export enum DELIVERY_STATUS {
  preparing = 'preparing',
  ready = 'ready',
  shipped = 'shipped',
  completed = 'completed',
}

export enum DELIVERY_TYPE {
  collect = 'collect',
  shipping = 'shipping',
}

/**
 * Calculate age based on birth date
 * @param birthDate
 * @returns the number of years (age)
 */
export const calculateAge = (birthDate?: Date): number => {
  if (birthDate) {
    return moment().diff(birthDate, 'years');
  }
  return 0;
};

/**
 * Returns the user's display name
 * @param user IUser object
 * @returns the user's name for display
 */
export const userDisplayName = (user: IUser): string => {
  if (!user) return '';

  let name = user.lastName;
  if (user.firstName) {
    name = user.firstName + ' ' + name;
  }
  return name;
};

/**
 * Returns the user's initials
 * @param user IUser object
 * @returns the user's name initials
 */
export const userInitials = (user: IUser): string => {
  if (!user) return '-';

  let initials = user.lastName.length > 0 ? user.lastName.charAt(0) : '';
  if (user.firstName && user.firstName.length > 0) {
    initials = user.firstName.charAt(0) + initials;
  }

  return initials.toUpperCase();
};

/**
 * Format date to string
 * @param d Date object to format
 * @returns formatted date for display
 */
export const formatDate = (d: Date): string => {
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date and time to string
 * @param d Date object to format
 * @returns formatted datetime for display
 */
export const formatDateTime = (d: Date): string => {
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Convert cents to SGD
 * @param cents
 * @returns formatted SGD amount
 */
export const centsToSGD = (cents: number): string => {
  if (cents === 0) {
    return '';
  }

  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'SGD',
  });
};

/**
 * Pluralize text based on the number provided
 * @param text
 * @param number
 * @returns pluralized text
 */
export const pluralize = (num: number, text: string): string => {
  return text + (num == 1 || num == -1 ? '' : 's');
};

/**
 * Returns the LOV description by ID
 * @param lovs
 * @param id
 * @returns string
 */
export const lovDesc = (lovs: ILOV[], id: number): string => {
  const lov = lovs.find((it) => it.id === id);

  return lov?.description ? lov.description : '-';
};

/**
 * Returns the LOV description by code
 * @param lovs
 * @param code
 * @returns string
 */
export const lovDescByCode = (lovs: ILOV[], code: string): string => {
  const lov = lovs.find((it) => it.code === code);

  return lov?.description ? lov.description : '-';
};

export const itemQualityDescCodeLookup: Map<string, string[]> = new Map<
  string,
  string[]
>([
  ['New', ['10']],
  ['Pristine', ['8', '9']],
  ['Good', ['7']],
  ['Vintage', ['1', '2', '3', '4', '5', '6']],
]);

/**
 * Returns the item quality LOV description by ID
 * @param lovs
 * @param id
 * @returns string
 */
export const itemQualityDesc = (lovs: ILOV[], id: number): string => {
  const lov = lovs.find((it) => it.id === id);
  if (!lov) {
    return '-';
  }

  let result = lov?.description ? lov.description : '-';
  itemQualityDescCodeLookup.forEach((codes, desc) => {
    if (codes.includes(lov.code)) {
      result = desc;
    }
  });

  return result;
};

/**
 * Returns the item quality description-based LOV
 * @returns ILOV[]
 */
export const getItemQualityDescLOV = (): ILOV[] => {
  return Array.from(itemQualityDescCodeLookup.keys()).map((desc) => {
    return { id: 0, code: desc, description: desc };
  });
};

/**
 * Returns the item's front image
 * @returns string
 */
export const getImageFront = (sku: string): string => {
  return itemImageBaseURL + sku + '_front.jpg';
};

/**
 * Returns the item's back image
 * @returns string
 */
export const getImageBack = (sku: string): string => {
  return itemImageBaseURL + sku + '_back.jpg';
};

/**
 * Returns the item's details image
 * @returns string
 */
export const getImageDetails = (sku: string): string => {
  return itemImageBaseURL + sku + '_details.jpg';
};

/**
 * Formats item details for display purposes
 * @param it:IItem
 * @returns IItem
 */
export const formatItemDisplay = (it: IItem) => {
  it.name = it.name ? startCase(toLower(it.name)) : it.name;

  if (it.metadata) {
    it.metadata.color = it.metadata.color
      ? startCase(toLower(it.metadata.color))
      : it.metadata.color;
  }

  return it;
};
